import React from 'react';

type GooeyTextProps = {
  texts: string[];
  morphTime?: number;
  cooldownTime?: number;
  className?: string;
  textClassName?: string;
  filterId?: string;
  align?: 'left' | 'center' | 'right';
};

let filterCounter = 0;

export default function GooeyText({
  texts,
  morphTime = 1,
  cooldownTime = 0.25,
  className = '',
  textClassName = '',
  filterId,
  align = 'center',
}: GooeyTextProps) {
  const wrapperRef = React.useRef<HTMLSpanElement>(null);
  const text1Ref = React.useRef<HTMLSpanElement>(null);
  const text2Ref = React.useRef<HTMLSpanElement>(null);
  const idRef = React.useRef<string>(filterId ?? `gooey-threshold-${++filterCounter}`);
  const [isInView, setIsInView] = React.useState(false);

  React.useEffect(() => {
    const node = wrapperRef.current;
    if (!node || typeof window === 'undefined') return undefined;
    if (!('IntersectionObserver' in window)) {
      setIsInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => setIsInView(entry.isIntersecting), {
      rootMargin: '120px',
      threshold: 0,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      if (text1Ref.current && text2Ref.current) {
        text1Ref.current.textContent = texts[0] ?? '';
        text2Ref.current.textContent = '';
        text1Ref.current.style.opacity = '100%';
        text2Ref.current.style.opacity = '0%';
      }
      return undefined;
    }

    if (!isInView) return undefined;

    let textIndex = texts.length - 1;
    let time = new Date();
    let morph = 0;
    let cooldown = cooldownTime;
    let frameId = 0;

    const setMorph = (fraction: number) => {
      if (text1Ref.current && text2Ref.current) {
        text2Ref.current.style.filter = `blur(${Math.min(8 / fraction - 8, 100)}px)`;
        text2Ref.current.style.opacity = `${Math.pow(fraction, 0.4) * 100}%`;
        const inverse = 1 - fraction;
        text1Ref.current.style.filter = `blur(${Math.min(8 / inverse - 8, 100)}px)`;
        text1Ref.current.style.opacity = `${Math.pow(inverse, 0.4) * 100}%`;
      }
    };

    const doCooldown = () => {
      morph = 0;
      if (text1Ref.current && text2Ref.current) {
        text2Ref.current.style.filter = '';
        text2Ref.current.style.opacity = '100%';
        text1Ref.current.style.filter = '';
        text1Ref.current.style.opacity = '0%';
      }
    };

    const doMorph = () => {
      morph -= cooldown;
      cooldown = 0;
      let fraction = morph / morphTime;
      if (fraction > 1) {
        cooldown = cooldownTime;
        fraction = 1;
      }
      setMorph(fraction);
    };

    const animate = () => {
      frameId = requestAnimationFrame(animate);
      const newTime = new Date();
      const shouldIncrementIndex = cooldown > 0;
      const dt = (newTime.getTime() - time.getTime()) / 1000;
      time = newTime;
      cooldown -= dt;

      if (cooldown <= 0) {
        if (shouldIncrementIndex) {
          textIndex = (textIndex + 1) % texts.length;
          if (text1Ref.current && text2Ref.current) {
            text1Ref.current.textContent = texts[textIndex % texts.length] ?? '';
            text2Ref.current.textContent = texts[(textIndex + 1) % texts.length] ?? '';
          }
        }
        doMorph();
      } else {
        doCooldown();
      }
    };

    let warmup: number | null = null;
    const startNow = () => {
      frameId = requestAnimationFrame(animate);
    };

    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    if (typeof idle === 'function') {
      warmup = idle(startNow, { timeout: 1500 });
    } else {
      warmup = window.setTimeout(startNow, 200) as unknown as number;
    }

    return () => {
      cancelAnimationFrame(frameId);
      if (warmup !== null) {
        const cancelIdle = (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback;
        if (typeof cancelIdle === 'function') cancelIdle(warmup);
        else clearTimeout(warmup);
      }
    };
  }, [texts, morphTime, cooldownTime, isInView]);

  return (
    <span ref={wrapperRef} className={`relative block w-full h-full ${className}`.trim()}>
      <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
        <defs>
          <filter id={idRef.current}>
            <feColorMatrix
              in="SourceGraphic"
              type="matrix"
              values="1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                      0 0 0 255 -140"
            />
          </filter>
        </defs>
      </svg>

      <span
        className={`absolute inset-0 flex items-center ${align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center'}`}
        style={{ filter: `url(#${idRef.current})` }}
      >
        <span ref={text1Ref} className={`absolute inline-block select-none ${align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center'} ${textClassName}`.trim()} aria-hidden="true" />
        <span ref={text2Ref} className={`absolute inline-block select-none ${align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center'} ${textClassName}`.trim()}>
          {texts[0]}
        </span>
      </span>

      <span className="sr-only">{texts.join(', ')}</span>
    </span>
  );
}
